import React, { ReactNode, useEffect, useMemo, useState } from "react";
import "./Tooltip.css";
import { Typography } from "../Typography";
import * as tokens from "../../design-tokens";

interface TooltipProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "content"> {
  direction?:
    | "top"
    | "top-left"
    | "top-right"
    | "bottom"
    | "bottom-left"
    | "bottom-right";
  content: ReactNode;
  customTestId?: string;
}

const Tooltip = ({
  className = "",
  direction = "top",
  content,
  children,
  customTestId,
  ...otherProps
}: TooltipProps) => {
  const [hide, setHide] = useState<"" | "hidetooltip">("");
  const id = useMemo(
    () => customTestId || `tip${new Date().getTime()}`,
    [customTestId]
  );

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();

        setHide("hidetooltip");
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  return (
    <div className={`sr-tooltip ${className}`} {...otherProps}>
      <div
        className={`sr-tooltip-children ${hide} ${hide} ${hide}`}
        aria-describedby={id}
        onFocus={() => setHide("")}
      >
        {children}
      </div>

      <div
        id={id}
        className={`sr-tooltip-content ${direction}`}
        role="tooltip"
        title={content as string}
      >
        <Typography color={tokens.INFO_30}>{content}</Typography>
      </div>
    </div>
  );
};

export { Tooltip };
export type { TooltipProps };
